import { Trash } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
  InfoCardRow,
} from '@/components/ui/InfoCard.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import {
  breakdownAmount,
  breakdownToSegments,
  keyedSegmentValue,
} from '@/components/charts/breakdownAdapter.ts';
import { MoneyBreakdownDto } from '@/types/stats/BreakdownDto.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import {
  BUDGET_SEGMENT_REGISTRY,
  BudgetSegmentKey,
} from '@/features/dashboard/tours/types/finance/budgetSegments.utils.ts';
import {
  financeMetricLabel,
  FinanceMetricKey,
} from '@/features/dashboard/tours/types/finance/financeMetrics.utils.ts';
import { findMetric, formatMetricValue } from '@/types/stats/metric.utils.ts';
import BreakdownHeadline from '@/features/dashboard/tours/components/finance/BreakdownHeadline.tsx';
import SegmentBreakdown from '@/features/dashboard/tours/components/finance/SegmentBreakdown.tsx';

interface BudgetCardProps {
  budget: MoneyBreakdownDto;
  /**
   * Czy w tej walucie ustawiono budżet. Waluta z samymi kosztami dostaje kartę
   * bez kosza — `DELETE` na nieistniejącym budżecie kończy się 404.
   */
  declared: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

/** Budżet kosztów w jednej walucie — jak saldo wpłat, waluty się nie sumują. */
const BudgetCard = ({
  budget,
  declared,
  onEdit,
  onDelete,
}: BudgetCardProps) => {
  const currencyLabel = getCurrencyLabel(budget.currency);
  const spent = keyedSegmentValue(budget, BudgetSegmentKey.SPENT);
  const perParticipant = findMetric(
    budget.metrics,
    FinanceMetricKey.BUDGET_PER_PARTICIPANT,
  );
  const revenuePerParticipant = findMetric(
    budget.metrics,
    FinanceMetricKey.REVENUE_PER_PARTICIPANT,
  );

  return (
    <InfoCard>
      <InfoCardHeader
        title={currencyLabel}
        aside={
          <span className="ml-auto flex shrink-0 items-center gap-1">
            <ActionButton tone="ghost" size="xs" onClick={onEdit}>
              {declared ? 'Edytuj budżet' : `Ustaw budżet w ${currencyLabel}`}
            </ActionButton>
            {declared && (
              <ActionButton
                tone="dangerGhost"
                size="xs"
                iconOnly
                title="Usuń budżet"
                onClick={onDelete}
              >
                <Trash />
              </ActionButton>
            )}
          </span>
        }
      >
        <span className="mt-1 block text-[13px] text-neutral-400">
          {declared
            ? `koszty w ${currencyLabel}`
            : 'Nie ustawiono budżetu w tej walucie'}
        </span>
      </InfoCardHeader>

      <InfoCardBody className="gap-3.5">
        <BreakdownHeadline
          value={breakdownAmount(budget, spent)}
          total={breakdownAmount(budget, budget.total)}
          dimmed={spent === 0}
        />

        <SegmentBreakdown
          total={breakdownAmount(budget, budget.total)}
          segments={breakdownToSegments(budget, BUDGET_SEGMENT_REGISTRY)}
        />

        <InfoCardRow
          label={financeMetricLabel(FinanceMetricKey.BUDGET_PER_PARTICIPANT)}
          divided
        >
          <span className="font-semibold tabular-nums text-base-text">
            {perParticipant ? (
              formatMetricValue(perParticipant)
            ) : (
              <EmptyValue />
            )}
          </span>
        </InfoCardRow>

        <InfoCardRow
          label={financeMetricLabel(FinanceMetricKey.REVENUE_PER_PARTICIPANT)}
        >
          <span className="font-semibold tabular-nums text-base-text">
            {revenuePerParticipant ? (
              formatMetricValue(revenuePerParticipant)
            ) : (
              <EmptyValue />
            )}
          </span>
        </InfoCardRow>
      </InfoCardBody>
    </InfoCard>
  );
};

export default BudgetCard;
